'use client';

import Link from '@eliadmin/core/src/components/link';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import type { UseFormReturn } from 'react-hook-form';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { z } from 'zod';

import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { Paragraph } from '../ui/paragraph';
import { BsKey } from 'react-icons/bs';
import { MdAlternateEmail, MdOutlinePhoneInTalk } from 'react-icons/md';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { useQuery } from '@tanstack/react-query';
import { HiOutlineArrowRightCircle } from 'react-icons/hi2';
import { Field } from '@eliadmin/core/src/components/form/field';
import { Heading } from '../ui/heading';

export type RegisterFormValues = z.infer<ReturnType<typeof registerSchema>>;

export const registerSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),

      address: z.string().optional(),
      street_number: z.string().optional(),
      postcode: z.string().optional(),
      city: z.string().optional(),
      country: z
        .object({
          id: z.string().or(z.number()).optional(),
        })
        .optional(),

      company_buy: z.boolean().optional(),
      company: z.string().optional(),
      ico: z.string().optional(),
      dic: z.string().optional(),
      ic_dph: z.string().optional(),
      email_company: z.string().optional(),
      address_company: z.string().optional(),
      street_number_company: z.string().optional(),
      postcode_company: z.string().optional(),
      city_company: z.string().optional(),
      country_company: z
        .object({
          id: z.string(),
        })
        .optional(),

      gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: z.boolean().optional(),

      newsletter_return_url: z.string(),
      tos: z.boolean().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': z.string().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

export function RegisterForm({
  initialSocialAuthProviders,
  onShowSignIn,
  inDialog = false,
}: {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  onShowSignIn?: () => void;
  inDialog?: boolean;
}) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const form = useForm<z.infer<ReturnType<typeof registerSchema>>>({
    resolver: zodResolver(registerSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,

      address: '',
      street_number: '',
      postcode: '',
      city: '',
      country: {
        id: 'SK',
      },

      company_buy: false,
      company: '',
      ico: '',
      dic: '',
      ic_dph: '',
      email_company: '',
      address_company: '',
      street_number_company: '',
      postcode_company: '',
      city_company: '',
      country_company: undefined,

      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
    },
  });

  const { toast } = useToast();
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    /** Check password validity */
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    try {

      const resp = await register({ ...data,  locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text654'),
        variant: 'success',
      });
      router.push(ROUTES.CONTACT());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const handlePasswordConfirmationMouseDown = () => {
    setShowPasswordConfirmation(true);
  };

  const handlePasswordConfirmationMouseUp = () => {
    setShowPasswordConfirmation(false);
  };

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex w-full flex-col items-center justify-center gap-8',
          'ea-register-form',
          !inDialog && 'py-12 sm:py-14 lg:py-16',
        )}
        onSubmit={handleSubmit}
      >
        <div className="flex w-full flex-col items-center justify-center gap-3">
          <Heading tag="h3" title={t('text3182')} />
          <Paragraph className="text-center" size="lg" color="muted-foreground">
            {t('text623')}&nbsp;
            <span
              className="cursor-pointer text-primary transition-all hover:opacity-80"
              onClick={inDialog ? onShowSignIn : () => router.push(ROUTES.SIGN_IN())}
            >
              {t('text624')}
            </span>
          </Paragraph>
        </div>

        <SocialAuthButtons
          className="ea-register-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
          newsletterAccepted={form.watch('newsletter' as any)}
          newsletterReturnUrl={form.watch('newsletter_return_url' as any)}
        />

        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-8',
            'ea-register-form__content',
          )}
        >
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <FormTitle name={t('text385')} />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className={cn('w-full', 'ea-register-form__email-field')}>
                  <FormControl>
                    <Input placeholder="E-mail *" type="email" variant={'transparent'} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem required className={cn('w-full', 'ea-register-form__password-field')}>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={`${t('text191')} *`}
                        variant={'transparent'}
                        type={showPassword ? 'text' : 'password'}
                        {...field}
                      />
                      <button
                        type="button"
                        onMouseDown={handlePasswordMouseDown}
                        onMouseUp={handlePasswordMouseUp}
                        onMouseLeave={handlePasswordMouseUp}
                        onTouchStart={handlePasswordMouseDown}
                        onTouchEnd={handlePasswordMouseUp}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                      >
                        {showPassword ? (
                          <FiEyeOff size={24} className="text-primary" />
                        ) : (
                          <FiEye size={24} className="text-primary" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password_confirmation"
              render={({ field }) => (
                <FormItem
                  required
                  className={cn('w-full', 'ea-register-form__password-confirmation-field')}
                >
                  <FormControl>
                    <div className="relative">
                      <Input
                        variant={'transparent'}
                        placeholder={`${t('text3183')} *`}
                        type={showPasswordConfirmation ? 'text' : 'password'}
                        {...field}
                      />
                      <button
                        type="button"
                        onMouseDown={handlePasswordConfirmationMouseDown}
                        onMouseUp={handlePasswordConfirmationMouseUp}
                        onMouseLeave={handlePasswordConfirmationMouseUp}
                        onTouchStart={handlePasswordConfirmationMouseDown}
                        onTouchEnd={handlePasswordConfirmationMouseUp}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                      >
                        {showPassword ? (
                          <FiEyeOff size={24} className="text-primary" />
                        ) : (
                          <FiEye size={24} className="text-primary" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormSeparator />

          <div className="grid w-full grid-cols-12 gap-4">
            <FormTitle name={t('text2350')} className="col-span-12" />
            <FormField
              control={form.control}
              name="company_buy"
              render={({ field }) => (
                <FormItem
                  className={cn('ea-register-form__company-type-field', 'col-span-12 w-full')}
                >
                  <FormControl>
                    <RadioGroup
                      onValueChange={val => field.onChange(val === 'true')}
                      value={String(field.value)}
                      className="flex gap-6"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="false" className="h-8 w-8" />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm font-medium">
                          {t('text2234')}
                        </FormLabel>
                      </FormItem>

                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="true" className="h-8 w-8" />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm font-medium">
                          {t('text1432')}
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.watch('company_buy') === true ? (
              <CompanyFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            ) : (
              <PersonFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            )}
          </div>
          <FormSeparator />
          <div className="col-span-12 flex w-full flex-col items-start justify-start gap-4">
            <FormTitle name={t('text803')} />
            {form.watch('company_buy') === true && (
              <FormField
                control={form.control}
                name="email_company"
                render={({ field }) => (
                  <FormItem required className={cn('w-full', 'ea-register-form__phone-field')}>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder={'Email *'}
                        variant={'transparent'}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem required className={cn('w-full', 'ea-register-form__phone-field')}>
                  <FormControl>
                    <Input {...field} placeholder={`${t('text1258')} *`} variant={'transparent'} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-8',
            'ea-register-form__footer',
          )}
        >
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-start gap-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="font-medium">
                  {t('text264')}{' '}
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.(), locale, 'tos')}
                    className="text-underline font-medium"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text265')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-start gap-0',
                  'ea-register-form__gdpr-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(
                      ROUTES?.GDPR?.() ?? '/',
                      locale,
                      'gdpr',
                    )}
                    className="text-underline font-medium"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text364')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('hidden w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex w-full items-center justify-center">
            <Button
              type="submit"
              className={cn('', 'ea-register-form__submit-button')}
              isLoading={form.formState.isSubmitting}
            >
              {t('text758')} <HiOutlineArrowRightCircle size={24} />
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

function FormTitle({ name, className }: { name: string; className?: string }) {
  return (
    <Heading
      title={name}
      tag="h5"
      className={{ title: cn(className), container: 'col-span-12 w-full' }}
    />
  );
}

function FormSeparator() {
  return <div className="h-[1px] w-full bg-[#D1D1D1]" />;
}

function PersonFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-6', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text33')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="surname"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-6', 'ea-register-form__surname-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text34')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="address"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-7', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text157')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="street_number"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-5', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text438')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="postcode"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-4', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text40')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-8', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text39')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <Field
        control={form.control}
        type="select"
        name="country.id"
        label={t('text519')}
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}

function CompanyFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <FormField
        control={form.control}
        name="ico"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-6', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text42')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="ic_dph"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-6', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text44')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="dic"
        render={({ field }) => (
          <FormItem required className={cn('col-span-12 w-full', 'ea-register-form__name-field')}>
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text43')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="company"
        render={({ field }) => (
          <FormItem required className={cn('col-span-12 w-full', 'ea-register-form__name-field')}>
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text118')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-6', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text33')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="surname"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-6', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text34')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="address_company"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-6', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text38')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="street_number_company"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-6', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text438')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="postcode_company"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-5', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text40')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="city_company"
        render={({ field }) => (
          <FormItem
            required
            className={cn('col-span-12 w-full sm:col-span-7', 'ea-register-form__name-field')}
          >
            <FormControl>
              <Input variant={'transparent'} placeholder={`${t('text39')} *`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <Field
        control={form.control}
        type="select"
        name="country_company.id"
        label={t('text519')}
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}
